import { SparklesIcon } from "@storytell/bsky-icons";
import { type Component, createEffect, Show, Suspense } from "solid-js";
import { PricingModal } from "~/components/ui";
import { DebugElement, debugTheme } from "~/config/theme";
import { usePromptBarContext } from "../PromptBarContext";
import { useTooltipBehavior } from "../hooks";
import { useWire } from "~/wire";
import { TbHelpCircleFilled, TbLoader2 } from "solid-icons/tb";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { PromptBarButton } from "./PromptBarButton";
import { StButton } from "@storytell/ui/buttons";
import { A, useNavigate } from "@solidjs/router";
import { useBillingClient, useCallOptions, withBearerToken } from "@storytell/cp-client";
import type { IconTypes } from "solid-icons";

/**
 * Credits display component with pill and detailed modal
 * Pure credits display without streaming state concerns
 */
export const CreditsDisplay: Component = () => {
  const wire = useWire();
  const navigate = useNavigate();

  return (
    <Show when={wire.billing.show()}>
      <StPopover
        noArrow
        gutter={10}
        placement="top-end"
        content={
          <Suspense fallback={<div>Loading...</div>}>
            <div class="flex flex-col p-4">
              <div class="flex items-center justify-between gap-10 mb-5">
                <h2 class="text-base font-bold">{wire.billing.isProPlan() ? "Pro Plan" : "Explore Plan (Free)"}</h2>

                <button
                  type="button"
                  class="underline text-sm text-brand cursor-pointer focus:outline-none focus:underline-offset-2"
                  onClick={async () => {
                    if (wire.billing.isFreePlan()) {
                      navigate("/pricing");
                      return;
                    }

                    const client = useBillingClient();
                    const res = await client.createPortalSession(
                      {
                        returnUrl: `https://${APP_DOMAIN}/`,
                        customerId: wire.auth.user()?.id,
                      },
                      useCallOptions(withBearerToken(wire.auth.token())),
                    );
                    if (res.isOk() && res.value.session.url) {
                      window.location.href = res.value.session.url;
                    }
                  }}
                >
                  {wire.billing.isProPlan() ? "Manage" : "Upgrade"}
                </button>
              </div>
              <p class="mb-1">
                <span class="font-semibold">{wire.billing.credits().toLocaleString()}</span> credits in total
              </p>
              <p class="mb-5">
                <span class="font-semibold">{wire.billing.creditsUsedThisPeriod().toLocaleString()}</span> credits used
                this month
              </p>

              <Show
                when={!wire.auth.isGuest()}
                fallback={
                  <StButton as={A} href="/auth/signup" class="w-full">
                    Sign up to get {(10000).toLocaleString()} free credits
                  </StButton>
                }
              >
                <StButton as={A} href="/pricing">
                  Top up
                </StButton>
              </Show>

              <a
                class="inline-flex items-center gap-1 underline text-brand text-xs mt-4"
                href="https://go.storytell.ai/learn-credits"
                target="_blank"
                rel="noreferrer"
              >
                <TbHelpCircleFilled />
                Learn how credits work
              </a>
            </div>
          </Suspense>
        }
      >
        <StPopoverTrigger as={PromptBarButton} class="w-auto pr-2">
          <Suspense
            fallback={
              <>
                <TbLoader2 class="animate-spin" />
                <span class="text-sm font-medium">...</span>
              </>
            }
          >
            <SparklesIcon />
            <span class="text-sm font-medium">{wire.billing.credits().toLocaleString()}</span>
          </Suspense>
        </StPopoverTrigger>
      </StPopover>
    </Show>
  );
};
